import Vue from 'vue'
import Vuex from 'vuex'
import {Account, Stream} from './api/server.ts'
import Doc from './api/Doc.ts'

Vue.use(Vuex)

let externalApi: ExternalApi

interface ExternalApi {
  getStreams(): Promise<Stream[]>;
  getStream(id: number): Promise<Stream>
  setStreamDisabled(id: number, isDisabled: boolean): Promise<Stream>
  archiveStream(id: number): Promise<Stream>
  saveStream(stream: Partial<Stream>): Promise<Stream>
}

interface StoreState {
  isReady: boolean
  loadCount: number
  account: Account | null
  streams: Stream[] | null
  currentStream: Stream | null
  productRecommendations: boolean
}

const store = new Vuex.Store<StoreState>({
  state: {
    isReady: false,
    loadCount: 0,
    account: null,
    streams: null,
    currentStream: null,
    productRecommendations: false
  },
  mutations: {
    SET_EXTERNAL_API(state, api: ExternalApi) {
      externalApi = api
    },
    INCREMENT_LOAD_COUNT(state) {
      state.loadCount++
    },
    DECREMENT_LOAD_COUNT(state) {
      state.loadCount--
    },
    SET_ACCOUNT(state, account: Account) {
      state.account = account
      state.isReady = true
    },
    SET_STREAMS(state, streams: Stream[]) {
      state.streams = streams
    },
    SET_CURRENT_STREAM(state, stream: Stream) {
      state.currentStream = stream
    },
    SET_PRODUCT_RECOMMENDATIONS(state, value: boolean) {
      state.productRecommendations = value
    }
  },
  getters: {
    isDev() {
      return false
    },
    accountName(state) {
      return state.account && state.account.name
    }
  },
  actions: {
    loadStreams({commit}) {
      return loadBlock(async () => {
        const streams = await externalApi.getStreams()
        commit('SET_STREAMS', streams)
        return streams
      })
    },
    async getStream({commit}, id: number): Promise<Stream> {
      return loadBlock(async () => {
        return externalApi.getStream(id)
      })
    },
    async createStream({commit}, name: string): Promise<Stream> {
      return loadBlock(async () => {
        // Create initial stream
        const doc = new Doc({
          id: null,
          accountId: null,
          name: name,
          trigger: {
            id: '1',
            componentType: 'TRIGGER',
            triggerType: 'SESSION_EXPIRY',
            name: 'Trigger1',
            nextComponents: []
          }
        })
        const stream = await externalApi.saveStream({
          name: name,
          eventType: doc.trigger.triggerType,
          status: 'draft',
          isDisabled: false,
          definition: JSON.stringify(doc)
        })
        // Assign IDs inside document
        doc.updateId(stream.id)
        doc.accountId = stream.accountId
        stream.definition = JSON.stringify(doc)
        // Save again
        return await externalApi.saveStream(stream)
      })
    },
    async copyStream(
      {dispatch},
      opts: {id: number, name: string}
    ): Promise<Stream> {
      return loadBlock(async () => {
        const source = await dispatch('getStream', opts.id)
        const copy = await dispatch('createStream', opts.name)
        const doc = Doc.fromJson(source.definition)
        doc.updateId(copy.id)
        doc.name = copy.name
        copy.eventType = doc.trigger.triggerType
        copy.definition = JSON.stringify(doc)
        return await externalApi.saveStream(copy)
      })
    },
    async setStreamDisabledById(
      {commit},
      opts: {id: number, isDisabled: boolean}
    ): Promise<Stream> {
      return loadBlock(async () => {
        return await externalApi.setStreamDisabled(opts.id, opts.isDisabled)
      })
    },
    async archiveStreamById({commit}, id: number): Promise<Stream> {
      return loadBlock(async () => {
        return await externalApi.archiveStream(id)
      })
    },
    async saveCurrentStream({commit}, stream: Stream): Promise<Stream> {
      return loadBlock(async () => {
        const result = await externalApi.saveStream(stream)
        commit('SET_CURRENT_STREAM', result)
        return result
      })
    },
    async editStreamById({commit}, id: number) {
      return loadBlock(async () => {
        const stream = await externalApi.getStream(id)
        commit('SET_CURRENT_STREAM', stream)
        return stream
      })
    },
    async publishStream({dispatch}, stream: Stream): Promise<Stream> {
      return loadBlock(async () => {
        const copy = Object.assign({}, stream)
        copy.status = 'published'
        return dispatch('saveCurrentStream', copy)
      })
    }
  }
})

async function loadBlock<T>(fn: () => Promise<T>) {
  store.commit('INCREMENT_LOAD_COUNT')
  const result = await fn()
  store.commit('DECREMENT_LOAD_COUNT')
  return result
}

export default store
